"use client";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { useCallback, useState } from "react";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import useAuth from "@/hooks/use-auth";
import { useAuthDialogStore } from "@/hooks/use-auth-dialog";

const formSchema = z
  .object({
    email: z.string().email({ message: "Invalid email address" }),
    password: z.string().min(8, {
      message: "Password must be at least 8 characters",
    }).optional(),
    repeat: z.string().min(8, {
      message: "Passwords must match",
    }).optional(),
    otp: z.string().min(6, {
      message: "OTP must be 6 digits",
    }).optional(),
  })
  .refine((data) => data.password === data.repeat, {
    path: ["repeat"],
    message: "Passwords must match",
  });

const emailSchema = z.object({
  email: z.string().email({ message: "Invalid email address" }),
});

const resetPasswordSchema = z
  .object({
    email: z.string().email({ message: "Invalid email address" }),
    otp: z.string().min(6, { message: "OTP must be 6 digits" }),
    password: z.string().min(8, { message: "Password must be at least 8 characters" }),
    repeat: z.string().min(8, { message: "Passwords must match" }),
  })
  .refine((data) => data.password === data.repeat, {
    path: ["repeat"],
    message: "Passwords must match",
  });


const FormForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const { sendForgotPasswordCode, confirmResetPasswordUser } = useAuth();
  const { forgotPassword } = useAuthDialogStore();

  const form = useForm<z.infer<typeof resetPasswordSchema | typeof emailSchema>>({
    resolver: zodResolver(otpSent ? resetPasswordSchema : emailSchema),
    defaultValues: {
      email: "",
      password: "",
      repeat: "",
      otp: "",
    },
  });

  const handleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const onSubmit = useCallback(
    async (values: z.infer<typeof formSchema>) => {
      setLoading(true);
      try {
        if (!otpSent) {
          await sendForgotPasswordCode(values.email);
          setOtpSent(true);
          toast.success("OTP sent to your email");
        } else {
          await confirmResetPasswordUser(values.email, values.otp || "", values.password || "");
          toast.success("Password reset successful");
          forgotPassword.closeDialog();
        }
      } catch (error: any) {
        toast.error("Error", {
          description: error.message,
        });
      } finally {
        setLoading(false);
      }
    },
    [otpSent, forgotPassword.closeDialog, sendForgotPasswordCode, confirmResetPasswordUser]
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-7">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="your@example.com" {...field} disabled={otpSent} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {otpSent && (
          <>
            <FormField
              control={form.control}
              name="otp"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>OTP Code</FormLabel>
                  <FormControl>
                    <Input type="text" maxLength={6} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="********"
                        {...field}
                      />
                      <div
                        className="absolute right-2 top-2 cursor-pointer"
                        onClick={handleShowPassword}
                      >
                        {showPassword ? <EyeOffIcon size={20} /> : <EyeIcon size={20} />}
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="repeat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Repeat New Password</FormLabel>
                  <FormControl>
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="********"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
        <Button type="submit" className="w-full text-lg font-semibold" variant={"default"} disabled={loading}>
          {otpSent ? "Reset Password" : "Continue"}
        </Button>
      </form>
    </Form>
  );
};

export default FormForgotPassword;